import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from "@tanstack/react-query";
import { IPaginatedResponse } from "../models/Pagination";
import {
  ICreateSupplieslogistics,
  IQuerySuppliesLogistics,
  ISuppliesLogistics,
  IUpdateSuppliesLogistics,
} from "../models/Logistics";
import { logisticsService } from "../services/logisticsService";

export function useGetSupplierLogistics(
  query: IQuerySuppliesLogistics,
): UseQueryResult<IPaginatedResponse<ISuppliesLogistics>> {
  return useQuery({
    queryKey: ["supplier-logistics", query],
    queryFn: () => logisticsService.getSupplierLogistics(query),
    enabled: !!query,
  });
}
export function useSearchSupplierLogistics(
  query: string,
): UseQueryResult<ISuppliesLogistics> {
  return useQuery({
    queryKey: ["supplier-logistics-search", query],
    queryFn: () => logisticsService.searchSupplierLogistics(query),
    enabled: !!query,
  });
}

export function useCreateSupplierLogistics(): UseMutationResult<
  ISuppliesLogistics,
  unknown,
  ICreateSupplieslogistics,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: ICreateSupplieslogistics) =>
      logisticsService.createSupplierLogistics(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["supplier-logistics"] });
    },
  });
}

export function useUpdateSupplierLogistics(): UseMutationResult<
  ISuppliesLogistics,
  unknown,
  {
    id: number;
    data: IUpdateSuppliesLogistics;
    query: IQuerySuppliesLogistics;
  },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      id: number;
      data: IUpdateSuppliesLogistics;
      query: IQuerySuppliesLogistics;
    }) => logisticsService.updateSupplierLogistics(body.id, body.data),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<IPaginatedResponse<ISuppliesLogistics>>(
        ["supplier-logistics", variables.query],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.map((item) =>
              item.id === data.id ? data : item,
            ),
          };
        },
      );
    },
  });
}

export function useDeleteSupplierLogistics(): UseMutationResult<
  void,
  unknown,
  { id: number; query: IQuerySuppliesLogistics },
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: { id: number; query: IQuerySuppliesLogistics }) =>
      logisticsService.deleteSupplierLogistics(body.id),
    onSuccess: (_, variable) => {
      queryClient.setQueryData<IPaginatedResponse<ISuppliesLogistics>>(
        ["supplier-logistics", variable.query],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: oldData.data.filter((item) => item.id !== variable.id),
          };
        },
      );
    },
  });
}
